import EventRepeatOutlinedIcon from '@mui/icons-material/EventRepeatOutlined'
import { Box, FormHelperText, InputAdornment, Stack, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { DatePicker, TimePicker } from '@mui/x-date-pickers'
import React, { useRef } from 'react'
import { useDateFormatter } from '../../generic/hooks/useDateFormatter'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import {
  changePeriodicForm,
  createEmptyPeriodicRule,
  formatLocalDate,
  formatLocalTime,
  isValidWeekInterval,
  parseLocalDate,
  parseLocalTime,
  PERIODIC_FORM,
  sortWeekdays
} from './periodicReturnRule'
import { periodicReturnSummary } from './periodicReturnSummary'

/**
 * Story 182 (issue #3015) — the control of a PERIODIC return rule of an external job: the form (relative to
 * the dispatch, or a recurring weekly series), the day(s), the interval in weeks, the time and, for a series,
 * its start date; plus the readable sentence of the rule.
 *
 * Mounted ONLY in PERIODIC mode, for the same reason as `ReturnDateField`: `useDateFormatter` reads
 * `dateFormats` off the MUI theme, so a panel that called it unconditionally would depend on a themed
 * provider even for jobs that show no date at all.
 *
 * Patterns reused from `CalendarRepeatComponent` (day `key`s MO..SU, ToggleButtonGroup of days, the error
 * texts), but NOT the component: it is wired to UNTIL (an end of series, which this story excludes), has no
 * time nor a relative form, and serialises to an RRULE shared with calendars and with the LINK's WHEN.
 *
 * What travels is the frozen contract shape. `time` ('HH:mm') and `seriesStart` ('yyyy-MM-dd') are LOCAL
 * STRINGS: the pickers speak `Date`, so every hop goes through the local parts of the date
 * (`periodicReturnRule.js`), never through an ISO instant, which would move the day across zones.
 *
 * Errors are inline and never block the edition (same as CalendarRepeatComponent); the save check is C1.
 *
 * Required fields carry the asterisk. The time cannot be left empty: a new rule starts at
 * `DEFAULT_RETURN_TIME` and clearing the picker keeps the last valid time.
 *
 * Layout: the node panel is narrow, so the seven days are a 7-column grid of compact pills (short weekday
 * name from Intl, the full translated name as accessible name and tooltip) and the inputs flow in a grid
 * that wraps instead of overflowing.
 *
 * @param {Object} props
 * @param {Object|null} props.rule - the `periodicRule` of the job's `externalReturn`; null while nothing is chosen
 * @param {boolean} props.disabled
 * @param {function(Object)} props.onChange - receives the whole new rule
 * @param {boolean} [props.embedded] - inside a popover: no own border nor tint, the popover paper is the card
 * @param {{current: Object}} [props.draftsRef] - where the per-form drafts live; a host that unmounts this
 *   editor (a popover closing) passes its own ref so the drafts outlive the editor
 */
const DAY_KEYS = [
  { code: 'MO', label: 'Monday' },
  { code: 'TU', label: 'Tuesday' },
  { code: 'WE', label: 'Wednesday' },
  { code: 'TH', label: 'Thursday' },
  { code: 'FR', label: 'Friday' },
  { code: 'SA', label: 'Saturday' },
  { code: 'SU', label: 'Sunday' }
]

const PickerField = { size: 'small', fullWidth: true, InputLabelProps: { shrink: true } }

// A Monday (2024-01-01) at noon local time: adding 0..6 days walks MO..SU without crossing a DST edge.
const REFERENCE_MONDAY = new Date(2024, 0, 1, 12)

/**
 * The visible label of a day button: the short weekday name of the UI language (Intl), so the seven days fit
 * in the side panel without new dictionary keys. Falls back to the first three letters of the full name if
 * Intl does not know the locale.
 */
const shortDayLabel = (index, language, fullName) => {
  try {
    const date = new Date(REFERENCE_MONDAY)
    date.setDate(REFERENCE_MONDAY.getDate() + index)
    return new Intl.DateTimeFormat((language || 'en').replace('_', '-'), { weekday: 'short' }).format(date).replace('.', '')
  } catch (error) {
    return fullName.slice(0, 3)
  }
}

const sectionSx = (embedded) => (theme) => embedded
  ? { p: 2 }
  : {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 2,
      p: 1.5,
      backgroundColor: alpha(theme.palette.primary.main, 0.03)
    }

const formGroupSx = {
  '& .MuiToggleButton-root': { flex: 1, textTransform: 'none', fontWeight: 600, py: 0.75 }
}

const dayGroupSx = (missing) => (theme) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
  gap: 0.75,
  '& .MuiToggleButtonGroup-grouped': {
    m: 0,
    minWidth: 0,
    height: 36,
    px: 0,
    border: `1px solid ${missing ? theme.palette.error.main : theme.palette.divider} !important`,
    borderRadius: '18px !important',
    textTransform: 'capitalize',
    fontSize: '0.8rem',
    fontWeight: 600,
    color: theme.palette.text.secondary,
    transition: theme.transitions.create(['background-color', 'border-color', 'color'], { duration: 150 })
  },
  '& .MuiToggleButtonGroup-grouped.Mui-selected': {
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.main,
    borderColor: `${theme.palette.primary.main} !important`
  },
  '& .MuiToggleButtonGroup-grouped.Mui-selected:hover': {
    backgroundColor: theme.palette.primary.dark
  }
})

const fieldsGridSx = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
  gap: 1.5,
  alignItems: 'start'
}

const summarySx = (theme) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 1,
  px: 1.5,
  py: 1,
  borderRadius: 1.5,
  backgroundColor: alpha(theme.palette.primary.main, 0.1),
  borderLeft: `3px solid ${theme.palette.primary.main}`
})

const PeriodicReturnField = ({ rule, disabled, onChange, embedded = false, draftsRef }) => {
  const { t_, i18n } = useSmartTranslation()
  const { format, formatDate } = useDateFormatter()

  const form = rule?.form
  const knownForm = form === PERIODIC_FORM.RELATIVE || form === PERIODIC_FORM.SERIES
  const weekdays = sortWeekdays(rule?.weekdays)
  const isSeries = form === PERIODIC_FORM.SERIES

  const update = (changes) => onChange({ ...(rule ?? createEmptyPeriodicRule(form)), ...changes })

  // The last rule edited in each form while the editor is open. Switching form still emits only the active
  // form's rule (RN29: the stored rule is replaced), but going back to a form already filled in restores what
  // it had instead of starting over; the first visit to a form follows RN29.1 (changePeriodicForm).
  const ownDrafts = useRef({})
  const draftsByForm = draftsRef ?? ownDrafts
  if (knownForm) {
    draftsByForm.current[form] = rule
  }

  const changeForm = (event, newForm) => {
    // An exclusive group reports null when the pressed button was the selected one: the form stays.
    if (newForm === null || newForm === form) {
      return
    }
    onChange(draftsByForm.current[newForm] ?? changePeriodicForm(rule, newForm))
  }

  const changeDays = (event, newDays) => {
    if (isSeries) {
      update({ weekdays: sortWeekdays(newDays) })
    } else {
      update({ weekdays: newDays ? [newDays] : [] })
    }
  }

  const changeInterval = (event) => {
    const raw = event.target.value
    update({ weekInterval: raw === '' ? null : Number(raw) })
  }

  const intervalInvalid = rule?.weekInterval !== null && rule?.weekInterval !== undefined && !isValidWeekInterval(rule.weekInterval)
  const weekdaysMissing = weekdays.length === 0
  const daysLabel = isSeries ? t_('Repeat week days') : t_('Return day')
  const intervalLabel = isSeries ? t_('Repeat every') : t_('Weeks after the dispatch week')

  const changeTime = (newValue) => {
    const time = formatLocalTime(newValue)
    // Cleared or half typed: keep the last valid time, the field is never left empty.
    if (time !== null) {
      update({ time })
    }
  }

  const summary = periodicReturnSummary(rule, { t_, formatDate, language: i18n?.language })

  return (
    <Box sx={sectionSx(embedded)} data-testid='periodic-return-field'>
      <Stack spacing={1.5}>
        <Stack direction='row' spacing={1} alignItems='center'>
          <EventRepeatOutlinedIcon fontSize='small' color='primary' />
          <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>{t_('Return rule')}</Typography>
        </Stack>

        <ToggleButtonGroup
          color='primary'
          size='small'
          exclusive
          fullWidth
          value={knownForm ? form : null}
          onChange={changeForm}
          aria-label={t_('Return rule')}
          disabled={disabled}
          sx={formGroupSx}
        >
          <ToggleButton value={PERIODIC_FORM.RELATIVE} disabled={disabled}>{t_('Relative to dispatch')}</ToggleButton>
          <ToggleButton value={PERIODIC_FORM.SERIES} disabled={disabled}>{t_('Recurring series')}</ToggleButton>
        </ToggleButtonGroup>

        {knownForm
          ? (
            <>
              <Box>
                <Typography variant='caption' color='text.secondary' component='div' sx={{ mb: 0.75 }}>
                  {daysLabel}
                  <Box component='span' aria-hidden sx={{ color: weekdaysMissing ? 'error.main' : 'inherit' }}> *</Box>
                </Typography>
                <ToggleButtonGroup
                  color='primary'
                  size='small'
                  exclusive={!isSeries}
                  value={isSeries ? weekdays : (weekdays[0] ?? null)}
                  onChange={changeDays}
                  aria-label={daysLabel}
                  disabled={disabled}
                  sx={dayGroupSx(weekdaysMissing)}
                >
                  {DAY_KEYS.map((day, index) => {
                    const fullName = t_(day.label)
                    return (
                      <ToggleButton key={day.code} value={day.code} disabled={disabled} aria-label={fullName}>
                        <Tooltip title={fullName} placement='top' arrow disableInteractive>
                          <span>{shortDayLabel(index, i18n?.language, fullName)}</span>
                        </Tooltip>
                      </ToggleButton>
                    )
                  })}
                </ToggleButtonGroup>
                {weekdaysMissing
                  ? <FormHelperText error>{t_('You must select at least one week day')}</FormHelperText>
                  : null}
              </Box>

              <Box sx={fieldsGridSx}>
                {/* The label is long for the narrow popover ("Weeks after the dispatch week"): the tooltip shows it
                    whole. describeChild keeps the input's accessible name as the label only. */}
                <Tooltip title={intervalLabel} placement='top' arrow disableInteractive describeChild>
                  <TextField
                    label={intervalLabel}
                    type='number'
                    size='small'
                    fullWidth
                    required
                    value={rule?.weekInterval ?? ''}
                    onChange={changeInterval}
                    disabled={disabled}
                    error={intervalInvalid}
                    helperText={intervalInvalid ? t_('Must be bigger than 0') : ''}
                    inputProps={{ min: 1 }}
                    InputProps={isSeries ? { endAdornment: <InputAdornment position='end'>{t_('Weeks')}</InputAdornment> } : undefined}
                    InputLabelProps={{ shrink: true }}
                  />
                </Tooltip>
                <TimePicker
                  label={t_('Return time')}
                  ampm={false}
                  format='HH:mm'
                  value={parseLocalTime(rule?.time)}
                  onChange={changeTime}
                  disabled={disabled}
                  slotProps={{ textField: { ...PickerField, required: true } }}
                />
                {isSeries
                  ? (
                    <DatePicker
                      label={t_('Start date')}
                      format={format.date}
                      value={parseLocalDate(rule?.seriesStart)}
                      onChange={(newValue) => update({ seriesStart: formatLocalDate(newValue) })}
                      disabled={disabled}
                      slotProps={{ textField: { ...PickerField, required: true } }}
                    />
                    )
                  : null}
              </Box>

              {summary
                ? (
                  <Box sx={summarySx}>
                    <EventRepeatOutlinedIcon fontSize='small' color='primary' sx={{ mt: '1px' }} />
                    <Typography variant='body2' color='text.primary' data-testid='periodic-return-summary'>{summary}</Typography>
                  </Box>
                  )
                : null}
            </>
            )
          : null}
      </Stack>
    </Box>
  )
}

export default PeriodicReturnField
